import { BackgroundCheckStatus } from '@paid-ui/enums/account';
import { ContractPartyTag } from '@paid-ui/enums/party';

export const isJoinedByLinkedContract = (party: { tags?: readonly ContractPartyTag[] }): boolean =>
  party.tags?.includes(ContractPartyTag.JOIN_BY_LINKED_CONTRACT) ?? false;

/**
 * Whether the group passed its background check, which is what the blue ID verified
 * tick reflects. A group can hold a beneficiary account without passing, and a group
 * that passed may hold no account yet, so this is not the same as having an account.
 */
export const isGroupBackgroundCheckPassed = (
  group?: { backgroundCheckStatus?: BackgroundCheckStatus } | null,
): boolean => group?.backgroundCheckStatus === BackgroundCheckStatus.PASS;

/** {@link isGroupBackgroundCheckPassed} for the group behind a contract party. */
export const isBackgroundCheckPassed = (
  party?: { userGroup?: { backgroundCheckStatus?: BackgroundCheckStatus } | null } | null,
): boolean => isGroupBackgroundCheckPassed(party?.userGroup);

export const isNonContractualParty = (party: { tags?: readonly ContractPartyTag[] }): boolean =>
  party.tags?.includes(ContractPartyTag.NON_CONTRACTUAL_PARTY) ?? false;

/** Whether `party` was invited by `inviter`. */
export const isInvitedBy = (
  party: { inviterParty?: { id: string } } | null | undefined,
  inviter: { id: string } | null | undefined,
): boolean => !!inviter?.id && party?.inviterParty?.id === inviter.id;

/**
 * Whether the signed-in user may resend `party`'s email invite, as contract service decides it.
 * A contractual party can only be re-invited by a member of its inviter party
 * (`DefaultReinviteValidator`). A non-contractual party (funder, QS) can be re-invited by anyone
 * managing the contract (`NonContractualReinviteValidator`, relaxed in PWA-12856).
 */
export const canReinviteParty = (
  party: { inviterParty?: { id: string }; tags?: readonly ContractPartyTag[] } | null | undefined,
  currentParty: { id: string } | null | undefined,
): boolean => (!!party && isNonContractualParty(party)) || isInvitedBy(party, currentParty);
